import { AlertTriangle } from 'lucide-react';
import type * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';

export interface ConfirmDialogProps {
  open: boolean;
  /**
   * Called when the dialog wants to close (cancel, escape, or after confirm).
   * Should drive the ``open`` prop. Ignored while ``loading`` is true so
   * users can't dismiss a destructive action mid-flight.
   */
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Optional secondary line shown under the title. Accepts ``ReactNode`` so
   * callers can highlight names with ``<code>`` etc. */
  description?: React.ReactNode;
  /** Defaults to ``"Confirm"`` (or ``"Delete"`` for ``destructive``). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** ``"destructive"`` shows the red icon + red CTA. Defaults to it because
   * confirm dialogs are almost always destructive in this app. */
  variant?: 'destructive' | 'default';
  /** Fires when the user clicks the confirm button. The caller is responsible
   * for closing the dialog (typically via ``onOpenChange(false)``) once the
   * underlying work resolves. */
  onConfirm: () => void;
  /** Disables both buttons and renders a spinner inside the confirm CTA. */
  loading?: boolean;
  /**
   * Optional inline error surface. When set, the dialog stays open and
   * renders a red call-out below the description so the user sees why
   * a destructive action didn't go through and can retry (the alternative
   * — silently closing the dialog or just letting the promise reject —
   * reads as "delete does nothing"). Cleared by the caller on retry /
   * dismiss.
   */
  errorMessage?: React.ReactNode;
}

/** Reusable confirmation modal — replacement for ``window.confirm``.
 *
 * Built on the Radix ``Dialog`` primitive so it inherits focus trapping,
 * escape handling, and the overlay-context portal logic from
 * ``components/ui/dialog.tsx`` (i.e. it lands inside the AgentPanel /
 * ResizableOverlay container instead of the document body). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'destructive',
  onConfirm,
  loading = false,
  errorMessage,
}: ConfirmDialogProps) {
  const resolvedConfirmLabel = confirmLabel ?? (variant === 'destructive' ? 'Delete' : 'Confirm');
  const isDestructive = variant === 'destructive';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md p-5">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full',
                isDestructive ? 'bg-red-100 dark:bg-red-500/20' : 'bg-gray-100 dark:bg-white/10'
              )}
            >
              <AlertTriangle
                className={cn(
                  'size-4',
                  isDestructive ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
                )}
              />
            </div>
            <DialogTitle className="text-[16px]">{title}</DialogTitle>
          </div>
          {description && (
            <DialogDescription className="pt-1 text-[13px] text-gray-600 dark:text-gray-400">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {errorMessage && (
          <div
            role="alert"
            className={cn(
              'mt-3 rounded-md border px-3 py-2 text-[12.5px] leading-relaxed',
              'border-red-200 bg-red-50 text-red-700',
              'dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
            )}
          >
            {errorMessage}
          </div>
        )}
        <DialogFooter className="mt-4 flex-row justify-end gap-2 sm:space-x-0">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={isDestructive ? 'destructive' : 'default'} size="sm" onClick={onConfirm} disabled={loading}>
            {loading && <Spinner size="size-3.5" className="mr-1.5" />}
            {resolvedConfirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
